import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { Client } from "@microsoft/microsoft-graph-client";
import { QuestionMarkCircleIcon } from "@heroicons/react/24/outline";
import { useData } from "../../providers/DataProvider";
import logo from "../../assets/images/logo-white.png";
import { useQueryClient } from "@tanstack/react-query";
import { bonusKeys } from "../../hooks/useBonusQueries";
import { useQBATheme } from "../../theme/QBAThemeContext";
import { Theme, THEMES } from "../../hooks/useTheme";

const normalizeRole = (role: string): string => {
  if (role === "DM-Review") return "DM";
  if (role === "VP-Review") return "VP";
  return role;
};

const Header: React.FC = () => {
  const { accounts, instance } = useMsal();
  const { setRole, loadDataForRole } = useData();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { theme, setTheme } = useQBATheme();

  const [isRoleMenuOpen, setIsRoleMenuOpen]     = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen]     = useState(false);
  const [userPhoto, setUserPhoto]               = useState<string | null>(null);
  const [selectedRole, setSelectedRole]         = useState<string>(
    () => normalizeRole(localStorage.getItem("selectedRole") || "")
  );

  // Separate refs — desktop and mobile user menus must not share a ref
  const desktopRoleRef    = useRef<HTMLDivElement>(null);
  const mobileRoleRef     = useRef<HTMLDivElement>(null);
  const mobileMenuRef     = useRef<HTMLDivElement>(null);
  const mobileToggleBtnRef = useRef<HTMLButtonElement>(null);
  const desktopUserRef    = useRef<HTMLDivElement>(null);
  const mobileUserBtnRef  = useRef<HTMLButtonElement>(null);
  const mobileUserMenuRef = useRef<HTMLDivElement>(null);

  // ── User info ────────────────────────────────────────────────────────────
  const availableRoles = useMemo(() => {
    const roleMap: Record<string, string> = {
      "DCCM-Review": "DCCM",
      "SPM-Review": "SPM",
      "VP-Review": "VP",
      "DM-Review": "DM",
    };

    const roles = [
      ...new Set(
        ((accounts[0]?.idTokenClaims?.roles as string[]) || []).map(
          (role) => roleMap[role] || role
        )
      ),
    ].sort();

    if (!roles) return [];
    return roles
      .filter(r => r !== "Admin" && r !== "DCCM-Review" && r !== "SPM-Review")
      .map(normalizeRole)
      .filter((role, index, arr) => arr.indexOf(role) === index)
      .sort((a, b) => a.localeCompare(b));
  }, [accounts]);

  const userName  = (accounts?.[0]?.name ?? "").trim();
  const userEmail = (accounts?.[0]?.username ?? "").trim();
  const initials  = userName
    .split(" ")
    .filter((w: string) => /^[a-zA-Z]/.test(w))
    .map((w: string) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase() || "?";

  // ── Fetch profile photo (same logic as SignOutButton) ────────────────────
  useEffect(() => {
    const fetchPhoto = async () => {
      if (!accounts[0]) return;
      try {
        const token = await instance.acquireTokenSilent({
          scopes: ["User.Read"],
          account: accounts[0],
        });
        const client = Client.init({
          authProvider: done => done(null, token.accessToken),
        });
        const response = await client.api("/me/photo/$value").get();
        if (response) setUserPhoto(URL.createObjectURL(response));
      } catch {
        setUserPhoto(null);
      }
    };
    fetchPhoto();
  }, [accounts, instance]);

  // ── Initialize role ──────────────────────────────────────────────────────
  useEffect(() => {
    const storedRole = localStorage.getItem("selectedRole");
    const normalizedStoredRole = normalizeRole(storedRole || "");
    if (normalizedStoredRole && normalizedStoredRole !== selectedRole) {
      setSelectedRole(normalizedStoredRole);
      setRole(normalizedStoredRole);
      loadDataForRole(normalizedStoredRole);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Outside click — each menu has its own ref group ──────────────────────
  useEffect(() => {
    const handle = (e: PointerEvent) => {
      const t = e.target as Node;

      // Role menu
      if (!desktopRoleRef.current?.contains(t) && !mobileRoleRef.current?.contains(t))
        setIsRoleMenuOpen(false);

      // Hamburger menu
      if (!mobileMenuRef.current?.contains(t) && !mobileToggleBtnRef.current?.contains(t))
        setIsMobileMenuOpen(false);

      // User menu — desktop: click outside desktopUserRef closes it
      //             mobile:  click outside mobileUserBtnRef + mobileUserMenuRef closes it
      const inDesktopUser = desktopUserRef.current?.contains(t);
      const inMobileBtn   = mobileUserBtnRef.current?.contains(t);
      const inMobileMenu  = mobileUserMenuRef.current?.contains(t);
      if (!inDesktopUser && !inMobileBtn && !inMobileMenu)
        setIsUserMenuOpen(false);
    };
    window.addEventListener("pointerdown", handle, { passive: true });
    return () => window.removeEventListener("pointerdown", handle);
  }, []);

  useEffect(() => {
    const handle = () => {
      if (window.innerWidth >= 768) {
        setIsMobileMenuOpen(false);
        setIsRoleMenuOpen(false);
        setIsUserMenuOpen(false);
      }
    };
    window.addEventListener("resize", handle, { passive: true });
    return () => window.removeEventListener("resize", handle);
  }, []);

  useEffect(() => {
    const handle = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setIsRoleMenuOpen(false);
        setIsMobileMenuOpen(false);
        setIsUserMenuOpen(false);
      }
    };
    window.addEventListener("keydown", handle);
    return () => window.removeEventListener("keydown", handle);
  }, []);

  // ── Logout — called directly, no SignOutButton wrapper needed ────────────
  const handleLogout = useCallback(() => {
    instance.logoutRedirect({ postLogoutRedirectUri: "/" });
  }, [instance]);

  const toggleRoleMenu   = useCallback(() => setIsRoleMenuOpen(p => !p), []);
  const toggleMobileMenu = useCallback(() => setIsMobileMenuOpen(p => !p), []);
  const toggleUserMenu   = useCallback(() => setIsUserMenuOpen(p => !p), []);

  const handleRoleSelect = useCallback(async (newRole: string) => {
    if (newRole === selectedRole) { setIsRoleMenuOpen(false); setIsMobileMenuOpen(false); return; }
    try {
      setSelectedRole(newRole);
      setRole(newRole);
      localStorage.setItem("selectedRole", newRole);
      window.dispatchEvent(new CustomEvent("roleChanged", { detail: newRole }));
      setIsRoleMenuOpen(false);
      setIsMobileMenuOpen(false);
      await loadDataForRole(newRole);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: bonusKeys.all }),
        queryClient.invalidateQueries({ queryKey: ["templates"] }),
        queryClient.invalidateQueries({ queryKey: ["applications"] }),
        queryClient.invalidateQueries({ queryKey: ["actionItems"] }),
        queryClient.invalidateQueries({ queryKey: ["userReviews"] }),
      ]);
      navigate("/", { replace: true });
      await queryClient.refetchQueries({ queryKey: bonusKeys.all, type: "active" });
    } catch (error) {
      console.error("Error changing role:", error);
    }
  }, [selectedRole, setRole, loadDataForRole, queryClient, navigate]);

  // ── Sub-components ───────────────────────────────────────────────────────
  const RoleButton: React.FC<{ onClick: () => void; className?: string }> = ({ onClick, className = "" }) => (
    <button
      onClick={onClick} type="button"
      aria-expanded={isRoleMenuOpen} aria-haspopup="true" aria-label="Select role"
      className={`flex items-center justify-between font-medium py-2 px-4 rounded-md transition-opacity duration-150 ${className}`}
      style={{ background: "var(--primary)", color: "var(--primary-foreground)" }}
    >
      <span className="truncate mr-2">{selectedRole || "Select Role"}</span>
      <svg className={`w-4 h-4 flex-shrink-0 transition-transform duration-200 ${isRoleMenuOpen ? "rotate-180" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7"/>
      </svg>
    </button>
  );

  const RoleMenuItem: React.FC<{ role: string }> = ({ role }) => (
    <button
      onClick={() => handleRoleSelect(role)} role="menuitem" type="button"
      className={`block px-4 py-2 text-sm w-full text-left transition-colors duration-150 ${role === selectedRole ? "font-medium" : "hover:bg-gray-100"}`}
      style={role === selectedRole ? { background: "var(--accent)", color: "var(--accent-foreground)" } : { color: "var(--foreground)" }}
    >
      {role === selectedRole && <span className="mr-2">✓</span>}
      {role}
    </button>
  );

  // Avatar — shows photo if available, else initials
  const AvatarButton: React.FC<{ onClick: () => void; btnRef?: React.Ref<HTMLButtonElement>; size?: string }> = ({ onClick, btnRef, size = "w-9 h-9" }) => (
    <button
      ref={btnRef} onClick={onClick} type="button" aria-label="User menu"
      aria-expanded={isUserMenuOpen} aria-haspopup="true"
      className={`${size} rounded-full overflow-hidden border-2 border-white/30 hover:border-white/60 focus:outline-none focus:ring-2 focus:ring-ring transition-all duration-150 flex-shrink-0`}
    >
      {userPhoto
        ? <img src={userPhoto} alt={userName} className="w-full h-full object-cover"/>
        : <div className="w-full h-full bg-white/20 hover:bg-white/30 flex items-center justify-center text-white text-sm font-semibold">{initials}</div>
      }
    </button>
  );

  // Theme + sign-out panel — shared between desktop and mobile user menus
  const UserMenuContent: React.FC<{ onThemeSelect?: () => void }> = ({ onThemeSelect }) => (
    <>
      {/* User info */}
      <div className="px-4 py-3 border-b" style={{ borderColor: "var(--border)" }}>
        <div className="flex items-center gap-2.5">
          {userPhoto
            ? <img src={userPhoto} alt={userName} className="w-8 h-8 rounded-full object-cover flex-shrink-0"/>
            : <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0" style={{ background: "var(--muted)", color: "var(--muted-foreground)" }}>{initials}</div>
          }
          <div className="min-w-0">
            <p className="text-sm font-semibold truncate" style={{ color: "var(--card-foreground)" }}>{userName || "User"}</p>
            <p className="text-xs truncate" style={{ color: "var(--muted-foreground)" }}>{userEmail}</p>
          </div>
        </div>
      </div>

      {/* Theme picker */}
      {/* <div className="px-3 py-3 border-b border-gray-100">
        <p className="text-[10px] font-semibold tracking-widest uppercase text-gray-400 mb-2 px-1">Theme</p>
        <div className="flex flex-col gap-0.5">
          {THEMES.map(t => (
            <button
              key={t.id} type="button"
              onClick={() => { setTheme(t.id as Theme); onThemeSelect?.(); }}
              className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-left transition-colors duration-100 ${theme === t.id ? "bg-gray-100" : "hover:bg-gray-50"}`}
            >
              <div className="flex gap-1 flex-shrink-0">
                {t.preview.map((color, i) => (
                  <div key={i} style={{ width: 13, height: 13, borderRadius: 3, background: color, border: "1px solid rgba(0,0,0,0.10)" }}/>
                ))}
              </div>
              <span className="text-sm font-medium text-gray-700 flex-1">{t.label}</span>
              {t.isDark && (
                <span className="text-[9px] font-bold uppercase tracking-wider bg-gray-700 text-white px-1.5 py-0.5 rounded">Dark</span>
              )}
              {theme === t.id && (
                <svg width="13" height="13" viewBox="0 0 13 13" fill="none" className="flex-shrink-0">
                  <path d="M2 6.5l3 3 6-6" stroke="#0D2D4A" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              )}
            </button>
          ))}
        </div>
      </div> */}

      {/* Help + Sign out */}
      <div className="px-3 py-2">
        <a
          href="/help"
          target="_blank"
          rel="noopener noreferrer"
          className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium hover:opacity-90 transition-opacity duration-150"
          style={{ color: "var(--foreground)" }}
        >
          <QuestionMarkCircleIcon className="w-4 h-4" />
          User Manual
        </a>
        <button
          onClick={handleLogout} type="button"
          className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium hover:opacity-90 transition-opacity duration-150"
          style={{ color: "var(--destructive)" }}
        >
          <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
            <path d="M6 2H3a1 1 0 00-1 1v9a1 1 0 001 1h3M10 10l3-3-3-3M13 7H6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Sign Out
        </button>
      </div>
    </>
  );

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <nav className="border-b px-4 py-2.5 fixed left-0 right-0 top-0 z-50" role="navigation" aria-label="Main navigation" style={{ background: "var(--primary)", borderColor: "var(--border)" }}>
      <div className="flex justify-between items-center max-w-screen-2xl mx-auto">

        {/* Left: hamburger + logo */}
        <div className="flex items-center min-w-0">
          <button
            ref={mobileToggleBtnRef} onClick={toggleMobileMenu} type="button"
            aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"} aria-expanded={isMobileMenuOpen}
            className="p-2 mr-2 text-white rounded-lg md:hidden hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-ring transition-opacity"
          >
            <svg className={`w-6 h-6 ${isMobileMenuOpen ? "hidden" : "block"}`} fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M3 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 15a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd"/>
            </svg>
            <svg className={`w-6 h-6 ${isMobileMenuOpen ? "block" : "hidden"}`} fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"/>
            </svg>
          </button>
          <Link to="/" className="flex items-center mr-4 min-w-0">
            <img src={logo} className="mr-2 sm:mr-3 h-8 sm:h-11 flex-shrink-0" alt="Highland Homes Logo"/>
            <span className="self-center text-base sm:text-lg md:text-xl font-semibold text-white truncate">Quarterly Bonus</span>
          </Link>
        </div>

        {/* ── Desktop right ──────────────────────────────────────────────── */}
        <div className="hidden md:flex items-center gap-3">
          {/* Role picker */}
          <div className="relative" ref={desktopRoleRef}>
            <RoleButton onClick={() => { setIsMobileMenuOpen(false); toggleRoleMenu(); }} className="min-w-[160px]"/>
            {isRoleMenuOpen && (
              <div className="absolute right-0 mt-2 py-2 w-48 rounded-md shadow-xl z-20 border" role="menu" style={{ background: "var(--card)", borderColor: "var(--border)" }}>
                {availableRoles.length > 0
                  ? availableRoles.map(role => <RoleMenuItem key={role} role={role}/>)
                  : <div className="px-4 py-2 text-sm" style={{ color: "var(--muted-foreground)" }}>No roles available</div>}
              </div>
            )}
          </div>

          {/* User menu — desktop */}
          <div className="relative" ref={desktopUserRef}>
            <AvatarButton onClick={toggleUserMenu}/>
            {isUserMenuOpen && (
              <div className="absolute right-0 mt-2 w-64 rounded-xl shadow-xl border z-30 overflow-hidden" style={{ background: "var(--card)", borderColor: "var(--border)" }}>
                <UserMenuContent/>
              </div>
            )}
          </div>
        </div>

        {/* ── Mobile right: avatar only ───────────────────────────────────── */}
        <div className="flex md:hidden items-center gap-2">
          <AvatarButton onClick={toggleUserMenu} btnRef={mobileUserBtnRef} size="w-8 h-8"/>
        </div>
      </div>

      {/* ── Mobile hamburger dropdown (role only) ──────────────────────── */}
      {isMobileMenuOpen && (
        <div ref={mobileMenuRef} className="absolute top-full left-0 right-0 z-50 shadow-md border-t" style={{ background: "var(--card)", borderColor: "var(--border)" }}>
          <div className="p-4">
            <p className="text-[10px] font-semibold tracking-widest uppercase mb-2" style={{ color: "var(--muted-foreground)" }}>Role</p>
            <div ref={mobileRoleRef}>
              <RoleButton onClick={toggleRoleMenu} className="w-full"/>
              {isRoleMenuOpen && (
                <div className="mt-2 py-1 w-full rounded-md border shadow-sm" role="menu" style={{ background: "var(--card)", borderColor: "var(--border)" }}>
                  {availableRoles.length > 0
                    ? availableRoles.map(role => <RoleMenuItem key={role} role={role}/>)
                    : <div className="px-4 py-2 text-sm" style={{ color: "var(--muted-foreground)" }}>No roles available</div>}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Mobile user menu (avatar tap) ──────────────────────────────── */}
      {isUserMenuOpen && (
        <div ref={mobileUserMenuRef} className="absolute top-full right-2 mt-1 w-64 rounded-xl shadow-xl border z-50 overflow-hidden md:hidden" style={{ background: "var(--card)", borderColor: "var(--border)" }}>
          <UserMenuContent onThemeSelect={() => setIsUserMenuOpen(false)}/>
        </div>
      )}
    </nav>
  );
};

export default Header;